/**
 * Add User wizard.
 *
 * CR-5313: rebuilt as a four-step flow so admins can:
 *   1. Capture the user profile up front (title, name, email, phone, job title, dept).
 *   2. Pick the primary company + any additional sites in the same group.
 *   3. Set per-site access (modules, role flags, notifications), with an
 *      "apply same settings to every site" toggle that drives bulk apply.
 *   4. Review the summary and send the invite.
 *
 * The legacy flow only supported one company and exposed the Compliance
 * module nowhere. This wizard layers on top of the existing
 * `/core/invitations/` endpoint plus the per_site_permissions JSON map
 * the backend now honours, so we don't need a UserProfile migration to
 * support multi-site assignments within a customer/supplier group.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  invitationAPI,
  coreAPI,
  type UserInvitationCreate,
  type UserSitePermissionFlags,
  type Role,
  type Company,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  UserPlus, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2,
  Building2, Truck, FileCheck, CheckCircle, ShieldCheck, UserCheck, Eye,
  ShieldAlert, Mail, Phone, Briefcase, User as UserIcon,
  ClipboardList, Layers,
} from 'lucide-react';

type WizardStep = 1 | 2 | 3 | 4;

const STEPS: { num: WizardStep; label: string; icon: typeof UserIcon }[] = [
  { num: 1, label: 'Profile', icon: UserIcon },
  { num: 2, label: 'Companies', icon: Building2 },
  { num: 3, label: 'Access', icon: ShieldCheck },
  { num: 4, label: 'Review', icon: ClipboardList },
];

const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof'];

const DEFAULT_PERMISSION_FLAGS: Required<UserSitePermissionFlags> = {
  can_purchasing: true,
  can_quality: true,
  can_compliance: false,
  is_admin: false,
  can_approve: false,
  is_read_only: false,
  notify_procurement: true,
  notify_quality: true,
  notify_compliance: false,
};

const DEPARTMENTS = [
  { value: 'admin', label: 'Admin (Full Access)' },
  { value: 'purchasing', label: 'Procurement' },
  { value: 'quality', label: 'Quality' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'engineering', label: 'Engineering' },
];

interface ProfileFields {
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  department: string;
  message: string;
  role: number | null;
}

const EMPTY_PROFILE: ProfileFields = {
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_title: '',
  department: '',
  message: '',
  role: null,
};

export default function InviteUser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteType = (searchParams.get('type') || 'customer') as 'customer' | 'supplier';
  const isSupplierInvite = inviteType === 'supplier';

  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Step 1
  const [profile, setProfile] = useState<ProfileFields>(EMPTY_PROFILE);

  // Step 2
  const [companies, setCompanies] = useState<Company[]>([]);
  const [primaryCompanyId, setPrimaryCompanyId] = useState<number>(0);
  const [additionalSiteIds, setAdditionalSiteIds] = useState<Set<number>>(new Set());

  // Step 3
  const [roles, setRoles] = useState<Role[]>([]);
  const [defaultFlags, setDefaultFlags] = useState<Required<UserSitePermissionFlags>>(DEFAULT_PERMISSION_FLAGS);
  // CR-5313: when true the matrix shows one shared row of toggles; when
  // false each selected site gets its own override card. Maps to the
  // backend's per_site_permissions JSON.
  const [applyToAll, setApplyToAll] = useState(true);
  const [perSiteOverrides, setPerSiteOverrides] = useState<Record<number, Required<UserSitePermissionFlags>>>({});

  const TypeIcon = isSupplierInvite ? Truck : Building2;

  useEffect(() => {
    void loadCompanies();
  }, [inviteType]);

  useEffect(() => {
    if (primaryCompanyId) void loadRoles();
  }, [primaryCompanyId, inviteType]);

  // CR-5313: when the user toggles "apply same settings" off, seed each
  // selected site's overrides from the current defaults so they have a
  // sensible starting point instead of an empty matrix.
  useEffect(() => {
    if (applyToAll) return;
    const allIds = [primaryCompanyId, ...Array.from(additionalSiteIds)].filter(Boolean);
    setPerSiteOverrides(prev => {
      const next: Record<number, Required<UserSitePermissionFlags>> = {};
      for (const id of allIds) {
        next[id] = prev[id] ?? { ...defaultFlags };
      }
      return next;
    });
  }, [applyToAll, primaryCompanyId, additionalSiteIds]);

  const loadCompanies = async () => {
    try {
      setLoadingOptions(true);
      let list: Company[] = [];
      if (isSupplierInvite) {
        const res = await coreAPI.getCompanies({ company_type: 'supplier', page_size: 1000 });
        list = res.data.results || [];
      } else {
        const res = await coreAPI.getAccessibleCompanies();
        list = (res.data.results || (Array.isArray(res.data) ? res.data : [])) as Company[];
      }
      setCompanies(list);
      // Sensible default for primary: user's own company if it's in the
      // list, otherwise the first entry.
      const ownInList = user?.company && list.find(c => c.id === user.company);
      if (ownInList) setPrimaryCompanyId(ownInList.id);
      else if (list.length === 1) setPrimaryCompanyId(list[0].id);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies.');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadRoles = async () => {
    try {
      const companyType = isSupplierInvite ? 'supplier' : 'customer';
      const res = await coreAPI.getRoles({ company_type: companyType, is_active: true });
      setRoles(res.data.results || []);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  // CR-5313: only show sister sites in the primary's group as candidates
  // for the multi-site step. Sites in unrelated groups would fail the
  // UserSitePermission.clean() check on the backend.
  const primaryCompany = useMemo(
    () => companies.find(c => c.id === primaryCompanyId),
    [companies, primaryCompanyId],
  );
  const sisterSites = useMemo(() => {
    if (!primaryCompany) return [];
    if (!primaryCompany.group) return companies.filter(c => c.id !== primaryCompany.id);
    return companies.filter(
      c => c.id !== primaryCompany.id && c.group === primaryCompany.group,
    );
  }, [companies, primaryCompany]);

  const selectedSiteIds = useMemo(() => {
    const ids = new Set<number>(additionalSiteIds);
    if (primaryCompanyId) ids.add(primaryCompanyId);
    return Array.from(ids);
  }, [primaryCompanyId, additionalSiteIds]);

  const flagsForSite = (siteId: number): Required<UserSitePermissionFlags> => {
    if (applyToAll) return defaultFlags;
    return perSiteOverrides[siteId] ?? defaultFlags;
  };

  const setFlag = (siteId: number | null, key: keyof UserSitePermissionFlags, value: boolean) => {
    if (siteId === null || applyToAll) {
      setDefaultFlags(prev => ({ ...prev, [key]: value }));
      return;
    }
    setPerSiteOverrides(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] ?? defaultFlags), [key]: value },
    }));
  };

  const validateStep = (target: WizardStep): string | null => {
    if (target >= 2 || step >= 1) {
      if (!profile.first_name.trim()) return 'First name is required.';
      if (!profile.last_name.trim()) return 'Last name is required.';
      if (!profile.email.trim()) return 'Email is required.';
    }
    if (target >= 3 || step >= 2) {
      if (!primaryCompanyId) return 'Pick a primary company.';
    }
    return null;
  };

  const goTo = (target: WizardStep) => {
    setError(null);
    if (target > step) {
      const reason = validateStep(target);
      if (reason) {
        setError(reason);
        return;
      }
    }
    setStep(target);
  };

  const handleSubmit = async () => {
    setError(null);
    const reason = validateStep(4);
    if (reason) { setError(reason); return; }
    setSubmitting(true);

    // CR-5313: per_site_permissions only carries the rows where the
    // admin actually overrode the defaults — keeps the JSON tidy and
    // makes the backend's "fall back to invitation defaults" branch
    // the common case.
    const overridesPayload: Record<string, UserSitePermissionFlags> = {};
    if (!applyToAll) {
      for (const sid of selectedSiteIds) {
        const flags = perSiteOverrides[sid];
        if (!flags) continue;
        const diff: UserSitePermissionFlags = {};
        let dirty = false;
        (Object.keys(DEFAULT_PERMISSION_FLAGS) as (keyof UserSitePermissionFlags)[]).forEach(k => {
          if (flags[k] !== defaultFlags[k]) {
            (diff as Record<string, boolean>)[k] = flags[k] as boolean;
            dirty = true;
          }
        });
        if (dirty) overridesPayload[String(sid)] = diff;
      }
    }

    const payload: UserInvitationCreate = {
      email: profile.email.trim(),
      first_name: profile.first_name.trim(),
      last_name: profile.last_name.trim(),
      salutation: profile.salutation || undefined,
      phone: profile.phone.trim() || undefined,
      company: primaryCompanyId,
      role: profile.role,
      job_title: profile.job_title || undefined,
      department: profile.department || undefined,
      message: profile.message || undefined,
      site_ids: Array.from(additionalSiteIds),
      ...defaultFlags,
      per_site_permissions: applyToAll ? undefined : overridesPayload,
    };

    try {
      await invitationAPI.createInvitation(payload);
      setSuccess(true);
      setTimeout(() => navigate('/admin/users'), 1800);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { email?: string[]; error?: string; detail?: string } } };
      setError(
        axiosErr.response?.data?.email?.[0] ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        'Failed to send invitation. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />Back to User Management
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-primary/10 rounded-lg">
          <UserPlus className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <TypeIcon className="h-7 w-7" />
            Add {isSupplierInvite ? 'Supplier' : 'Customer'} User
          </h1>
          <p className="text-muted-foreground">
            Profile first, then companies, then per-site access.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-6">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const isCurrent = step === s.num;
          const isDone = step > s.num;
          return (
            <div key={s.num} className="flex items-center flex-1">
              <button
                type="button"
                onClick={() => { if (s.num < step) goTo(s.num); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isCurrent ? 'bg-primary text-primary-foreground font-semibold'
                    : isDone ? 'text-primary hover:bg-primary/10 cursor-pointer'
                      : 'text-muted-foreground'
                }`}
                disabled={s.num > step}
              >
                <Icon className="h-4 w-4" />
                <span>{s.num}. {s.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 ${step > s.num ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      {success && (
        <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            Invitation sent successfully. Redirecting…
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>1. User profile</CardTitle>
            <CardDescription>Who is this user? Company assignment comes next.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Select value={profile.salutation || 'none'}
                  onValueChange={v => setProfile({ ...profile, salutation: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="first_name">First name *</Label>
                <Input id="first_name" required value={profile.first_name}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="last_name">Last name *</Label>
                <Input id="last_name" required value={profile.last_name}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />Email *</Label>
                <Input id="email" type="email" required value={profile.email}
                  onChange={e => setProfile({ ...profile, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Telephone</Label>
                <Input id="phone" value={profile.phone}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job_title" className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" />Job title</Label>
                <Input id="job_title" value={profile.job_title}
                  onChange={e => setProfile({ ...profile, job_title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={profile.department || 'none'}
                  onValueChange={v => setProfile({ ...profile, department: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Welcome message (optional)</Label>
              <Textarea id="message" rows={3} value={profile.message}
                onChange={e => setProfile({ ...profile, message: e.target.value })}
                placeholder="Add a personal note for the invitee…" />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>2. Related companies</CardTitle>
            <CardDescription>
              Pick the primary company, then any additional sites in the same group the user should have access to.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Primary company *</Label>
              <Select value={primaryCompanyId ? String(primaryCompanyId) : ''}
                onValueChange={v => { setPrimaryCompanyId(parseInt(v)); setAdditionalSiteIds(new Set()); }}>
                <SelectTrigger><SelectValue placeholder="Select company…" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        {c.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The user's profile is anchored to this company. Other sites become additional access only.
              </p>
            </div>

            {primaryCompanyId > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5" />Additional sites in this group
                </Label>
                {sisterSites.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No sister sites available in this group.
                  </p>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {sisterSites.map(site => {
                      const checked = additionalSiteIds.has(site.id);
                      return (
                        <label key={site.id}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v: boolean | 'indeterminate') => {
                              const next = new Set(additionalSiteIds);
                              if (v) next.add(site.id); else next.delete(site.id);
                              setAdditionalSiteIds(next);
                            }}
                          />
                          <span className="flex-1 text-sm">{site.name}</span>
                          {site.country && (
                            <Badge variant="outline" className="text-[10px]">{site.country}</Badge>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Selected: {selectedSiteIds.length} site{selectedSiteIds.length === 1 ? '' : 's'} (including primary).
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={profile.role?.toString() || 'none'}
                onValueChange={v => setProfile({ ...profile, role: v === 'none' ? null : parseInt(v) })}>
                <SelectTrigger><SelectValue placeholder="Select role (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific role</SelectItem>
                  {roles.map(r => <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>3. Access configuration</CardTitle>
            <CardDescription>
              Modules, role flags, and notification preferences.
              Apply the same settings to every site, or override per site.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
              <div>
                <p className="font-medium text-sm">Apply same settings to every site</p>
                <p className="text-xs text-muted-foreground">
                  Turn off to fine-tune access per site below.
                </p>
              </div>
              <Switch checked={applyToAll} onCheckedChange={setApplyToAll} />
            </div>

            {applyToAll ? (
              <PermissionMatrix
                title="Default access (applies to every selected site)"
                flags={defaultFlags}
                onChange={(key, value) => setFlag(null, key, value)}
              />
            ) : (
              <div className="space-y-3">
                {selectedSiteIds.map(sid => {
                  const site = companies.find(c => c.id === sid);
                  const isPrimary = sid === primaryCompanyId;
                  return (
                    <PermissionMatrix
                      key={sid}
                      title={site?.name || `Site #${sid}`}
                      subtitle={isPrimary ? 'Primary company' : undefined}
                      flags={flagsForSite(sid)}
                      onChange={(key, value) => setFlag(sid, key, value)}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>4. Review &amp; send</CardTitle>
            <CardDescription>Sanity-check before the invitation email goes out.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <ReviewBlock label="Profile">
              <div>{profile.salutation && <span className="text-muted-foreground mr-1">{profile.salutation}</span>}<strong>{profile.first_name} {profile.last_name}</strong></div>
              <div className="text-muted-foreground">{profile.email}{profile.phone ? ` · ${profile.phone}` : ''}</div>
              {(profile.job_title || profile.department) && (
                <div className="text-xs text-muted-foreground mt-1">
                  {profile.job_title}{profile.job_title && profile.department ? ' · ' : ''}{profile.department}
                </div>
              )}
            </ReviewBlock>

            <ReviewBlock label="Companies">
              <div><strong>Primary:</strong> {primaryCompany?.name || '—'}</div>
              {additionalSiteIds.size > 0 && (
                <div className="mt-1">
                  <strong>Additional sites:</strong>
                  <ul className="list-disc list-inside text-muted-foreground">
                    {Array.from(additionalSiteIds).map(sid => (
                      <li key={sid}>{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                Role: {profile.role ? roles.find(r => r.id === profile.role)?.name : 'No specific role'}
              </div>
            </ReviewBlock>

            <ReviewBlock label="Access">
              <div className="text-xs text-muted-foreground mb-1">
                {applyToAll ? 'Same settings applied to every site' : 'Per-site overrides'}:
              </div>
              {applyToAll ? (
                <PermissionSummary flags={defaultFlags} />
              ) : (
                <div className="space-y-2">
                  {selectedSiteIds.map(sid => (
                    <div key={sid} className="border-l-2 border-primary/40 pl-2">
                      <div className="font-medium">{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</div>
                      <PermissionSummary flags={flagsForSite(sid)} />
                    </div>
                  ))}
                </div>
              )}
            </ReviewBlock>

            {profile.message && (
              <ReviewBlock label="Welcome message">
                <p className="whitespace-pre-wrap text-muted-foreground italic">{profile.message}</p>
              </ReviewBlock>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step nav */}
      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" disabled={step === 1 || submitting}
          onClick={() => goTo((step - 1) as WizardStep)}>
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>
        {step < 4 ? (
          <Button onClick={() => goTo((step + 1) as WizardStep)}>
            Next<ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || success}>
            {submitting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending…</>
            ) : (
              <><UserPlus className="h-4 w-4 mr-2" />Send invitation</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PermissionMatrixProps {
  title: string;
  subtitle?: string;
  flags: Required<UserSitePermissionFlags>;
  onChange: (key: keyof UserSitePermissionFlags, value: boolean) => void;
}

function PermissionMatrix({ title, subtitle, flags, onChange }: PermissionMatrixProps) {
  return (
    <div className="border rounded-lg p-3 space-y-4">
      <div>
        <p className="font-medium text-sm">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PermissionGroup label="Modules">
          <FlagRow icon={<FileCheck className="h-3.5 w-3.5" />} label="Purchasing"
            checked={flags.can_purchasing} onChange={v => onChange('can_purchasing', v)} />
          <FlagRow icon={<CheckCircle className="h-3.5 w-3.5" />} label="Quality"
            checked={flags.can_quality} onChange={v => onChange('can_quality', v)} />
          <FlagRow icon={<ShieldAlert className="h-3.5 w-3.5" />} label="Compliance"
            checked={flags.can_compliance} onChange={v => onChange('can_compliance', v)} />
        </PermissionGroup>

        <PermissionGroup label="User role">
          <FlagRow icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Administrator"
            checked={flags.is_admin} onChange={v => onChange('is_admin', v)} />
          <FlagRow icon={<UserCheck className="h-3.5 w-3.5" />} label="Approver"
            checked={flags.can_approve} onChange={v => onChange('can_approve', v)} />
          <FlagRow icon={<Eye className="h-3.5 w-3.5" />} label="Read only"
            checked={flags.is_read_only} onChange={v => onChange('is_read_only', v)} />
        </PermissionGroup>

        <PermissionGroup label="Notifications">
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Procurement"
            checked={flags.notify_procurement} onChange={v => onChange('notify_procurement', v)} />
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Quality"
            checked={flags.notify_quality} onChange={v => onChange('notify_quality', v)} />
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Compliance"
            checked={flags.notify_compliance} onChange={v => onChange('notify_compliance', v)} />
        </PermissionGroup>
      </div>
    </div>
  );
}

function PermissionGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FlagRow({ icon, label, checked, onChange }:
  { icon: React.ReactNode; label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-1.5">{icon}{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function PermissionSummary({ flags }: { flags: Required<UserSitePermissionFlags> }) {
  const modules: string[] = [];
  if (flags.can_purchasing) modules.push('Purchasing');
  if (flags.can_quality) modules.push('Quality');
  if (flags.can_compliance) modules.push('Compliance');
  const role: string[] = [];
  if (flags.is_admin) role.push('Administrator');
  if (flags.can_approve) role.push('Approver');
  if (flags.is_read_only) role.push('Read only');
  const notify: string[] = [];
  if (flags.notify_procurement) notify.push('Procurement');
  if (flags.notify_quality) notify.push('Quality');
  if (flags.notify_compliance) notify.push('Compliance');
  return (
    <div className="text-xs text-muted-foreground space-y-0.5">
      <div><strong>Modules:</strong> {modules.length ? modules.join(', ') : '—'}</div>
      <div><strong>Role:</strong> {role.length ? role.join(', ') : 'Standard user'}</div>
      <div><strong>Notifications:</strong> {notify.length ? notify.join(', ') : 'None'}</div>
    </div>
  );
}

function ReviewBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-md p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{label}</p>
      {children}
    </div>
  );
}
